import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import {
  getUserGroups,
  createGroup,
  inviteUserToGroup,
  getGroupInvites,
  getUserInvites,
  acceptGroupInvite,
  declineGroupInvite,
  getGroupMembers,
  leaveGroup,
  kickMemberFromGroup,
  getGroupActivity
} from '../store/social/thunks';
import '../styles/summary.css';
import '../styles/filter.css';
import { returnInTZ } from '../utils/timezone';
import GroupsSkeleton from './GroupsSkeleton';
import { FaUsers, FaBell, FaEnvelopeOpenText, FaUserMinus, FaSignOutAlt } from "react-icons/fa";
import {
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Center,
  Heading,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Input,
  useColorMode,
  Button,
  Box,
  FormControl,
  FormLabel,
  Badge,
  HStack,
  VStack,
  IconButton,
  Divider,
  Icon,
  Tooltip,
  useBreakpointValue,
} from "@chakra-ui/react";
import { AddIcon, EmailIcon } from "@chakra-ui/icons";

import useToastNotification from "../hooks/useToastNotification";
import useAsyncState from "../hooks/useAsyncState";
export default function Groups({ user }) {
  const showToast = useToastNotification();
  const dispatch = useDispatch();
  const { groups } = useSelector((state) => state.social);
  const { userInvites } = useSelector((state) => state.social);
  const hasGroups = ((groups && groups.groups && groups.groups.length > 0) ? (true):(false));
  const hasUserInvites = ((userInvites && userInvites.invites && userInvites.invites.length > 0) ? (true):(false));
  const { onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const { loading: authLoading, success, info } = useAsyncState({
    onError: (info) => {
      if (info?.response?.data?.result) {
        showToast(info.response.data.result, { status: "error" });
      } else if (info?.response?.data) {
        showToast(JSON.stringify(info.response.data), { status: "error" });
      } else {
        showToast("An error occurred", { status: "error" });
      }
    },
  });
  const { colorMode } = useColorMode();

  // Responsive heading size
  const headingSize = useBreakpointValue({ base: 'md', md: 'lg' });
  
  // Calculate dynamic table heights
  // Each row is approximately 40px, header is 40px
  // Max height for ~17 rows (17 * 40 + 40 = 720px)
  const calculateTableHeight = (itemCount) => {
    const rowHeight = 40; // Approximate height per row
    const headerHeight = 40; // Header height
    const minHeight = 200; // Minimum height for small lists
    const maxHeight = 680; // Max height for ~17 rows before scrolling
    
    const calculatedHeight = (itemCount * rowHeight) + headerHeight;
    
    // Return calculated height if less than max, otherwise return max (which enables scrolling)
    return Math.max(minHeight, Math.min(calculatedHeight, maxHeight));
  };
  
  const groupsTableHeight = hasGroups ? calculateTableHeight(groups.groups.length) : 200;
  const invitesTableHeight = hasUserInvites ? calculateTableHeight(userInvites.invites.length) : 200;

  // Dialog states
  const [createGroupDialog, setCreateGroupDialog] = useState(false);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [membersDialog, setMembersDialog] = useState(false);
  const [invitesDialog, setInvitesDialog] = useState(false);
  const [leaveDialog, setLeaveDialog] = useState(false);
  const [kickDialog, setKickDialog] = useState(false);
  
  // Selected group for operations
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [, setSelectedInvite] = useState(null);
  
  // Form states
  const [groupName, setGroupName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [kickEmail, setKickEmail] = useState("");
  
  // Data states
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupInvites, setGroupInvites] = useState([]);
  const [activityDialog, setActivityDialog] = useState(false);
  const [groupActivity, setGroupActivity] = useState([]);

  const formatTimestamp = (utcDate) => returnInTZ(utcDate, 'YYYY-MM-DD HH:mm:ss');

  useEffect(() => {
    dispatch(getUserGroups({}));
    dispatch(getUserInvites({}));
  }, []); 
  useEffect(() => {
    evaluateSuccess();
  }, [success, info]);

  const evaluateSuccess = async () => {
    if(success === true && info && info.result) {
      const resultMessage = info.result;
      if (resultMessage.includes("Group Created Successfully")) {
        showToast(resultMessage);
        setCreateGroupDialog(false);
        setGroupName("");
        await dispatch(getUserGroups({}));
      } else if (resultMessage.includes("User invited successfully")) {
        showToast(resultMessage);
        setInviteDialog(false);
        setInviteEmail("");
        setSelectedGroup(null);
      } else if (resultMessage.includes("Group invite accepted successfully")) {
        showToast(resultMessage);
        await dispatch(getUserGroups({}));
        await dispatch(getUserInvites({}));
      } else if (resultMessage.includes("Group invite declined successfully")) {
        showToast(resultMessage);
        await dispatch(getUserInvites({}));
      } else if (resultMessage.includes("Successfully left the group")) {
        showToast(resultMessage);
        setLeaveDialog(false);
        setSelectedGroup(null);
        await dispatch(getUserGroups({}));
      } else if (resultMessage.includes("Successfully removed")) {
        showToast(resultMessage);
        setKickDialog(false);
        setKickEmail("");
        setSelectedGroup(null);
        setGroupMembers([]);
      }
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) {
      showToast("Group name is required", { status: "error" });
      return;
    }
    await dispatch(createGroup({ name: groupName.trim() }));
  };

  const handleOpenInviteDialog = (group) => {
    setSelectedGroup(group);
    setInviteEmail("");
    setInviteDialog(true);
    onOpen();
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      showToast("Email is required", { status: "error" });
      return;
    }
    await dispatch(inviteUserToGroup({ group_id: selectedGroup.group_id, email: inviteEmail.trim() }));
  };

  const handleOpenMembersDialog = async (group) => {
    setSelectedGroup(group);
    const result = await dispatch(getGroupMembers({ group_id: group.group_id }));
    if (result.payload && result.payload.members) {
      setGroupMembers(result.payload.members);
    }
    setMembersDialog(true);
    onOpen();
  };

  const handleOpenInvitesDialog = async (group) => {
    setSelectedGroup(group);
    const result = await dispatch(getGroupInvites({ group_id: group.group_id }));
    if (result.payload && result.payload.invites) {
      setGroupInvites(result.payload.invites);
    }
    setInvitesDialog(true);
    onOpen();
  };

  const handleOpenLeaveDialog = (group) => {
    setSelectedGroup(group);
    setLeaveDialog(true);
    onOpen();
  };

  const handleLeaveGroup = async (e) => {
    e.preventDefault();
    await dispatch(leaveGroup({ group_id: selectedGroup.group_id }));
  };

  const handleOpenKickDialog = (group) => {
    setSelectedGroup(group);
    setKickEmail("");
    setKickDialog(true);
    onOpen();
  };

  const handleKickMember = async (e) => {
    e.preventDefault();
    if (!kickEmail.trim()) {
      showToast("Email is required", { status: "error" });
      return;
    }
    await dispatch(kickMemberFromGroup({ group_id: selectedGroup.group_id, email: kickEmail.trim() }));
    // Refresh members after kicking
    const result = await dispatch(getGroupMembers({ group_id: selectedGroup.group_id }));
    if (result.payload && result.payload.members) {
      setGroupMembers(result.payload.members);
    }
  };

  const handleAcceptInvite = async (invite) => {
    await dispatch(acceptGroupInvite({ invite_id: invite.invite_id }));
  };

  const handleDeclineInvite = async (invite) => {
    await dispatch(declineGroupInvite({ invite_id: invite.invite_id }));
  };

  const handleOpenActivityDialog = async (group) => {
    setSelectedGroup(group);
    const result = await dispatch(getGroupActivity({ group_id: group.group_id }));
    if (result.payload && result.payload.activity) {
      setGroupActivity(result.payload.activity);
    } else {
      setGroupActivity([]);
    }
    setActivityDialog(true);
    onOpen();
  };

  const closeAllDialogs = () => {
    setCreateGroupDialog(false);
    setInviteDialog(false);
    setMembersDialog(false);
    setInvitesDialog(false);
    setLeaveDialog(false);
    setKickDialog(false);
    setActivityDialog(false);
    setSelectedGroup(null);
    setSelectedInvite(null);
    setGroupName("");
    setInviteEmail("");
    setKickEmail("");
    setGroupMembers([]);
    setGroupInvites([]);
    setGroupActivity([]);
    onClose();
  };

  return (
    <Flex
      flexDirection="column"
      minHeight="100vh"
      backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}
    >
      <Box flex="auto" overflowX="auto" overflowY="auto">
        {authLoading ? (
          <VStack flex="auto" p={6} spacing={6} w="full" align="stretch" backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}>
            <GroupsSkeleton />
          </VStack>
        ) : (
          <VStack flex="auto" p={6} spacing={4} w="full" align="stretch" backgroundColor={colorMode === 'light' ? "gray.200" : "gray.800"}>
            <HStack justifyContent="space-between" mb="4">
              <Heading size={headingSize}>My Groups</Heading>
              <Button
                leftIcon={<AddIcon />}
                colorScheme="blue"
                size="sm"
                onClick={() => {
                  setCreateGroupDialog(true);
                  onOpen();
                }}
              >
                Create Group
              </Button>
            </HStack>

            {/* User's Groups */}
            <Box mb="6">
              {hasGroups ? (
                <Box
                  bg={colorMode === 'light' ? 'white' : 'gray.700'}
                  boxShadow="md"
                  rounded="lg"
                  pt={4} px={5} pb={5}
                  mx="auto"
                  w="80%"
                  maxW="800px"
                  overflow="hidden"
                  h={`${groupsTableHeight}px`}
                  maxH={`${groupsTableHeight}px`}
                >
                  <TableContainer overflowY="auto" maxHeight={`${groupsTableHeight}px`} height={`${groupsTableHeight}px`} rounded="lg">
                    <Table size='sm' variant='simple'>
                      <Thead position="sticky" top={0} zIndex={10} bgColor={colorMode === 'light' ? "gray.50" : "gray.600"}>
                        <Tr>
                          <Th borderColor={colorMode === 'light' ? 'gray.200' : 'gray.500'}>Name</Th>
                          <Th borderColor={colorMode === 'light' ? 'gray.200' : 'gray.500'}>Role</Th>
                          <Th borderColor={colorMode === 'light' ? 'gray.200' : 'gray.500'}>Created</Th>
                          <Th borderColor={colorMode === 'light' ? 'gray.200' : 'gray.500'}>Owner</Th>
                          <Th textAlign="center" borderColor={colorMode === 'light' ? 'gray.200' : 'gray.500'}>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {groups.groups.map((group) => (
                          <Tr key={group.group_id}>
                            <Td fontWeight="bold" borderColor={colorMode === 'light' ? 'gray.100' : 'gray.600'}>{group.name}</Td>
                            <Td borderColor={colorMode === 'light' ? 'gray.100' : 'gray.600'}>
                              <Badge colorScheme={group.my_role === 'owner' ? 'red' : 'blue'}>
                                {group.my_role}
                              </Badge>
                            </Td>
                            <Td borderColor={colorMode === 'light' ? 'gray.100' : 'gray.600'}>{formatTimestamp(group.created_at)}</Td>
                            <Td borderColor={colorMode === 'light' ? 'gray.100' : 'gray.600'}>{group.owner.first_name} {group.owner.last_name}</Td>
                            <Td borderColor={colorMode === 'light' ? 'gray.100' : 'gray.600'}>
                              <HStack spacing={1} justifyContent="center">
                                <Tooltip label="Members" fontSize="xs" hasArrow>
                                  <IconButton aria-label="Members" icon={<FaUsers />} size="xs" colorScheme="blue" variant="ghost" onClick={() => handleOpenMembersDialog(group)} />
                                </Tooltip>
                                <Tooltip label="Activity" fontSize="xs" hasArrow>
                                  <IconButton aria-label="Activity" icon={<FaBell />} size="xs" colorScheme="teal" variant="ghost" onClick={() => handleOpenActivityDialog(group)} />
                                </Tooltip>
                                {group.my_role === 'owner' && (
                                  <>
                                    <Tooltip label="Invite" fontSize="xs" hasArrow>
                                      <IconButton aria-label="Invite" icon={<EmailIcon />} size="xs" colorScheme="green" variant="ghost" onClick={() => handleOpenInviteDialog(group)} />
                                    </Tooltip>
                                    <Tooltip label="Pending Invites" fontSize="xs" hasArrow>
                                      <IconButton aria-label="Pending Invites" icon={<FaEnvelopeOpenText />} size="xs" colorScheme="purple" variant="ghost" onClick={() => handleOpenInvitesDialog(group)} />
                                    </Tooltip>
                                    <Tooltip label="Kick Member" fontSize="xs" hasArrow>
                                      <IconButton aria-label="Kick Member" icon={<FaUserMinus />} size="xs" colorScheme="orange" variant="ghost" onClick={() => handleOpenKickDialog(group)} />
                                    </Tooltip>
                                  </>
                                )}
                                {group.my_role !== 'owner' && (
                                  <Tooltip label="Leave Group" fontSize="xs" hasArrow>
                                    <IconButton aria-label="Leave Group" icon={<FaSignOutAlt />} size="xs" colorScheme="red" variant="ghost" onClick={() => handleOpenLeaveDialog(group)} />
                                  </Tooltip>
                                )}
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Center py={8}>
                  <VStack spacing={3}>
                    <Icon as={FaUsers} boxSize={8} color="gray.400" />
                    <Text color="gray.500" fontSize="sm">No groups yet. Create your first group to start sharing stats!</Text>
                  </VStack>
                </Center>
              )}
            </Box>

            <Divider />

            {/* User's Invites */}
            <Box mt="6">
              <Heading size={headingSize} mb="3">My Invites</Heading>
              {hasUserInvites ? (
                <Box
                  bg={colorMode === 'light' ? 'white' : 'gray.700'}
                  boxShadow="md"
                  rounded="lg"
                  pt={4} px={5} pb={5}
                  mx="auto"
                  w="80%"
                  maxW="800px"
                  overflow="hidden"
                  h={`${invitesTableHeight}px`}
                  maxH={`${invitesTableHeight}px`}
                >
                  <TableContainer overflowY="auto" maxHeight={`${invitesTableHeight}px`} height={`${invitesTableHeight}px`} rounded="lg">
                    <Table size='sm' variant='simple'>
                      <Thead position="sticky" top={0} zIndex={10} bgColor={colorMode === 'light' ? "gray.50" : "gray.600"}>
                        <Tr>
                          <Th borderColor={colorMode === 'light' ? 'gray.200' : 'gray.500'}>Group</Th>
                          <Th borderColor={colorMode === 'light' ? 'gray.200' : 'gray.500'}>Invited By</Th>
                          <Th borderColor={colorMode === 'light' ? 'gray.200' : 'gray.500'}>Status</Th>
                          <Th borderColor={colorMode === 'light' ? 'gray.200' : 'gray.500'}>Created</Th>
                          <Th textAlign="center" borderColor={colorMode === 'light' ? 'gray.200' : 'gray.500'}>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {userInvites.invites.map((invite) => (
                          <Tr key={invite.invite_id}>
                            <Td fontWeight="bold" borderColor={colorMode === 'light' ? 'gray.100' : 'gray.600'}>{invite.group_name}</Td>
                            <Td borderColor={colorMode === 'light' ? 'gray.100' : 'gray.600'}>{invite.invited_by.first_name} {invite.invited_by.last_name}</Td>
                            <Td borderColor={colorMode === 'light' ? 'gray.100' : 'gray.600'}>
                              <Badge colorScheme={invite.status === 'pending' ? 'yellow' : invite.status === 'accepted' ? 'green' : 'red'}>
                                {invite.status}
                              </Badge>
                            </Td>
                            <Td borderColor={colorMode === 'light' ? 'gray.100' : 'gray.600'}>{formatTimestamp(invite.created_at)}</Td>
                            <Td borderColor={colorMode === 'light' ? 'gray.100' : 'gray.600'}>
                              {invite.status === 'pending' && (
                                <HStack spacing={2} justifyContent="center">
                                  <Button size="xs" colorScheme="green" onClick={() => handleAcceptInvite(invite)}>
                                    Accept
                                  </Button>
                                  <Button size="xs" colorScheme="red" onClick={() => handleDeclineInvite(invite)}>
                                    Decline
                                  </Button>
                                </HStack>
                              )}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Center py={6}>
                  <VStack spacing={3}>
                    <Icon as={EmailIcon} boxSize={8} color="gray.400" />
                    <Text color="gray.500" fontSize="sm">No pending invites.</Text>
                  </VStack>
                </Center>
              )}
            </Box>
          </VStack>
        )}
      </Box>

      {/* Create Group Dialog */}
      <AlertDialog
        isOpen={createGroupDialog}
        leastDestructiveRef={cancelRef}
        onClose={closeAllDialogs}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Create New Group
            </AlertDialogHeader>
            <AlertDialogBody>
              <FormControl isRequired>
                <FormLabel>Group Name</FormLabel>
                <Input
                  placeholder="Enter group name"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                />
              </FormControl>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeAllDialogs}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleCreateGroup} ml={3}>
                Create
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Invite User Dialog */}
      <AlertDialog
        isOpen={inviteDialog}
        leastDestructiveRef={cancelRef}
        onClose={closeAllDialogs}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Invite User to {selectedGroup?.name}
            </AlertDialogHeader>
            <AlertDialogBody>
              <FormControl isRequired>
                <FormLabel>Email Address</FormLabel>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </FormControl>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeAllDialogs}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleInviteUser} ml={3}>
                Invite
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Group Members Dialog */}
      <AlertDialog
        isOpen={membersDialog}
        leastDestructiveRef={cancelRef}
        onClose={closeAllDialogs}
      >
        <AlertDialogOverlay>
          <AlertDialogContent maxW="fit-content" minW="md">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Members of {selectedGroup?.name}
            </AlertDialogHeader>
            <AlertDialogBody>
              <TableContainer maxHeight="400px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Role</Th>
                      <Th>Status</Th>
                      <Th>Joined</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {groupMembers.map((member, index) => (
                      <Tr key={index}>
                        <Td>{member.first_name} {member.last_name}</Td>
                        <Td>{member.email}</Td>
                        <Td>
                          <Badge colorScheme={member.role === 'owner' ? 'red' : 'blue'}>
                            {member.role}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={member.status === 'active' ? 'green' : 'gray'}>
                            {member.status}
                          </Badge>
                        </Td>
                        <Td>{formatTimestamp(member.joined_at)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeAllDialogs}>
                Close
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Group Invites Dialog */}
      <AlertDialog
        isOpen={invitesDialog}
        leastDestructiveRef={cancelRef}
        onClose={closeAllDialogs}
        size="xl"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Invites for {selectedGroup?.name}
            </AlertDialogHeader>
            <AlertDialogBody>
              <TableContainer maxHeight="400px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Invited User</Th>
                      <Th>Invited By</Th>
                      <Th>Status</Th>
                      <Th>Created</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {groupInvites.map((invite) => (
                      <Tr key={invite.invite_id}>
                        <Td>{invite.invited_user.first_name} {invite.invited_user.last_name} ({invite.invited_user.email})</Td>
                        <Td>{invite.invited_by.first_name} {invite.invited_by.last_name}</Td>
                        <Td>
                          <Badge colorScheme={invite.status === 'pending' ? 'yellow' : invite.status === 'accepted' ? 'green' : 'red'}>
                            {invite.status}
                          </Badge>
                        </Td>
                        <Td>{formatTimestamp(invite.created_at)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeAllDialogs}>
                Close
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Leave Group Dialog */}
      <AlertDialog
        isOpen={leaveDialog}
        leastDestructiveRef={cancelRef}
        onClose={closeAllDialogs}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Leave Group
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to leave "{selectedGroup?.name}"? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeAllDialogs}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleLeaveGroup} ml={3}>
                Leave
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Group Activity Dialog */}
      <AlertDialog
        isOpen={activityDialog}
        leastDestructiveRef={cancelRef}
        onClose={closeAllDialogs}
        size="xl"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Activity Feed — {selectedGroup?.name}
            </AlertDialogHeader>
            <AlertDialogBody>
              {groupActivity.length === 0 ? (
                <Text color="gray.500" textAlign="center" py={4}>No recent activity from group members.</Text>
              ) : (
                <VStack spacing={3} align="stretch" maxH="400px" overflowY="auto">
                  {groupActivity.map((item, idx) => (
                    <Box key={idx} p={3} borderWidth="1px" borderRadius="md" bg={colorMode === 'light' ? 'gray.50' : 'gray.700'}>
                      <HStack>
                        <Badge colorScheme={item.notification_type === 'streak_milestone' ? 'orange' : 'blue'} fontSize="0.7em">
                          {item.notification_type === 'streak_milestone' ? 'Milestone' : 'Summary'}
                        </Badge>
                        <Text fontSize="xs" color="gray.500">{new Date(item.created_at).toLocaleDateString()}</Text>
                      </HStack>
                      <Text fontSize="sm" fontWeight="bold" mt={1}>A member: {item.title}</Text>
                      {item.message && <Text fontSize="xs" color={colorMode === 'light' ? 'gray.600' : 'gray.300'} mt={1}>{item.message}</Text>}
                    </Box>
                  ))}
                </VStack>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeAllDialogs}>
                Close
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Kick Member Dialog */}
      <AlertDialog
        isOpen={kickDialog}
        leastDestructiveRef={cancelRef}
        onClose={closeAllDialogs}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Kick Member from {selectedGroup?.name}
            </AlertDialogHeader>
            <AlertDialogBody>
              <FormControl isRequired>
                <FormLabel>Member Email</FormLabel>
                <Input
                  type="email"
                  placeholder="member@example.com"
                  value={kickEmail}
                  onChange={(e) => setKickEmail(e.target.value)}
                />
              </FormControl>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeAllDialogs}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleKickMember} ml={3}>
                Kick
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  )
}
